import { createSelector } from 'reselect'

import { StoreState, useStore } from '~/store/store'

import { useUnScheduledSurgeries } from '../'
import { COMMON_SURGERY_INCLUDE, type CommonUnScheduledSurgery, selectSchedulingBookingIds } from './surgeries'

export type PreviewSurgery = CommonUnScheduledSurgery

export type PreviewSurgeryEntry = {
    surgery: PreviewSurgery
    isStrongMatch: boolean
    suggestionIndex?: number
    filtersAppliedCount?: number
}

const selectPreviewSurgeriesEntries = (state: StoreState) => state.operationalPlanner.previewSurgeries

const selectOptimisticPreviewEntries = createSelector(selectPreviewSurgeriesEntries, selectSchedulingBookingIds, (entries, schedulingBookingIds) => {
    if (schedulingBookingIds.length === 0) return entries
    const schedulingIds = new Set(schedulingBookingIds)
    return entries.filter(entry => !schedulingIds.has(entry.surgeryId))
})

/**
 * Storm-native equivalent of `selectPreviewSurgeries`: operational-planner preview
 * entries joined to unscheduled surgeries, excluding any booking currently in the
 * in-flight scheduling slice.
 */
export function usePreviewSurgeries(): PreviewSurgeryEntry[] {
    const optimisticPreviewEntries = useStore(selectOptimisticPreviewEntries)
    const previewBookingIds = optimisticPreviewEntries.map(entry => entry.surgeryId)

    const unscheduledByBookingId = useUnScheduledSurgeries({
        where: { bookingId: { in: previewBookingIds } },
        include: COMMON_SURGERY_INCLUDE,
        keyBy: 'bookingId',
    })

    const result: PreviewSurgeryEntry[] = []
    for (const entry of optimisticPreviewEntries) {
        const surgery = unscheduledByBookingId.get(entry.surgeryId)
        if (!surgery) continue
        result.push({
            surgery,
            isStrongMatch: entry.isStrongMatch,
            suggestionIndex: entry.suggestionIndex,
            filtersAppliedCount: entry.filtersAppliedCount,
        })
    }
    return result
}

/** Flat preview surgeries for hospital-constraint candidate-group requests. */
export function usePreviewSurgeryCandidates(): PreviewSurgery[] {
    return usePreviewSurgeries().map(({ surgery }) => surgery)
}

/**
 * bookingId → preview list index (for suggestion-card selection badges).
 * Mirrors legacy `selectPreviewSurgeries` join: only resolved preview entries
 * count, in the same order as `usePreviewSurgeries`.
 */
export function usePreviewSelectionByBookingId() {
    const previewSurgeries = usePreviewSurgeries()
    const map = new Map<string, number>()
    previewSurgeries.forEach((entry, index) => {
        map.set(entry.surgery.bookingId, index)
    })
    return map
}
